import { defineStore } from "pinia";
import { authDataService } from "@/services/AuthApi";
import { userProfileDataService } from "@/services/UserProfileApi";
import { appLogger } from "@/services/AppLogger";
import { IUserProfile, IFeatureFlagConfiguration, FeatureFlagState } from "@/models/userProfile.model"

const userIdKey = "userId";
const customerIdKey = "customerId";
const userFullNameKey = "userFullName";
const userFirstNameKey = "userFirstName";
const userLastNameKey = "userLastName";
const userPortalLinkKey = "userPortalLink";
const accessTokenKey = "accessToken";
const accessNonceKey = "accessNonce";
const beamerKey = "beamerKey";
const userflowKey = "userflowKey";
const knowledgeBaseLinkKey = "knowledgeBaseLink";
const featureDiscussionLinkKey = "featureDiscussionLink";
const helpdeskLinkKey = "helpdeskLink";
const isSidebarExpandedKey = "isSidebarExpanded";
const featureFlagsKey = 'featureFlags';

interface IUserProfileStore {
  userId: string | null,
  customerId: string | null,
  fullName: string | null,
  firstName: string | null,
  lastName: string | null,
  portalLink: string | null,
  accessToken: string | null,
  accessNonce: string | null,
  beamerKey: string | null,
  userflowKey: string | null,
  knowledgeBaseLink: string | null,
  featureDiscussionLink: string | null,
  helpdeskLink: string | null,
  isSidebarExpanded: boolean,
  featureFlags: IFeatureFlagConfiguration[],
}

export const useUserProfileStore = defineStore("userProfileStore", {
   state: (): IUserProfileStore => ({
    userId: localStorage.getItem(userIdKey),
    customerId: localStorage.getItem(customerIdKey),
    fullName: localStorage.getItem(userFullNameKey),
    firstName: localStorage.getItem(userFirstNameKey),
    lastName: localStorage.getItem(userLastNameKey),
    portalLink: localStorage.getItem(userPortalLinkKey),
    accessToken: localStorage.getItem(accessTokenKey),
    accessNonce: localStorage.getItem(accessNonceKey),
    beamerKey: localStorage.getItem(beamerKey),
    userflowKey: localStorage.getItem(userflowKey),
    knowledgeBaseLink: localStorage.getItem(knowledgeBaseLinkKey),
    featureDiscussionLink: localStorage.getItem(featureDiscussionLinkKey),
    helpdeskLink: localStorage.getItem(helpdeskLinkKey),
    isSidebarExpanded: JSON.parse(localStorage.getItem(isSidebarExpandedKey) || 'true'),
    featureFlags: JSON.parse(localStorage.getItem(featureFlagsKey) || '[]')
  }),
  getters: {
    showUserflow: (state) => {
      return !!state.userflowKey && !!state.userId && !!state.customerId;
    },
    showBeamer: (state) => {
      return !!state.beamerKey && !!state.userId && !!state.customerId;
    },
    featureFlag: (state) => (featureName: string) => {
      return state.featureFlags?.find((f: IFeatureFlagConfiguration) => f.name === featureName);
    },
    hasFeature: (state) => (featureName: string) => {
      featureName = featureName.toLowerCase();
      const feature = state.featureFlags?.find((f: IFeatureFlagConfiguration) => f.name.toLowerCase() === featureName);
      return feature?.state === FeatureFlagState.enabled;
    }
  },
  actions: {
    setAccessNonce(value: string) {
      let nonce = value?.toLowerCase();
      localStorage.setItem(accessNonceKey, nonce);
      this.accessNonce = nonce;
    },
    toggleSidebarState() {
      this.isSidebarExpanded = !this.isSidebarExpanded;
      localStorage.setItem(isSidebarExpandedKey, this.isSidebarExpanded.toString());
    },
    async setAccessToken(nonce: string) {
      const token = await authDataService.getInternalTokenAsync(nonce);
      if (token) {
        this.accessToken = token;
        localStorage.setItem(accessTokenKey, token);
        this.setAccessNonce(nonce);
      }
    },
    async setUserProfileAsync(): Promise<IUserProfile | null> {
      const userProfile = await userProfileDataService.getCurrentAsync();

      if (userProfile) {
        this.userId = userProfile.userId;
        localStorage.setItem(userIdKey, this.userId);

        this.customerId = userProfile.customerId;
        localStorage.setItem(customerIdKey, this.customerId);
        
        this.fullName = userProfile.fullName;
        localStorage.setItem(userFullNameKey, this.fullName);

        const [firstName, lastName] = userProfile.fullName.split(" ");
        this.firstName = firstName;
        localStorage.setItem(userFirstNameKey, this.firstName);
        this.lastName = lastName;
        localStorage.setItem(userLastNameKey, this.lastName);

        this.portalLink = userProfile.portalLink;
        localStorage.setItem(userPortalLinkKey, this.portalLink);
        this.featureFlags = userProfile.featureFlags;
        localStorage.setItem(featureFlagsKey, JSON.stringify(this.featureFlags));

        this.beamerKey = userProfile.beamerKey || '';
        localStorage.setItem(beamerKey, this.beamerKey);

        this.userflowKey = userProfile.userFlowKey || '';
        localStorage.setItem(userflowKey, this.userflowKey);

        this.knowledgeBaseLink = userProfile.knowledgeBaseLink || '';
        localStorage.setItem(knowledgeBaseLinkKey, this.knowledgeBaseLink);

        this.featureDiscussionLink = userProfile.featureDiscussionLink || '';
        localStorage.setItem(featureDiscussionLinkKey, this.featureDiscussionLink);

        this.helpdeskLink = userProfile.helpdeskLink || '';
        localStorage.setItem(helpdeskLinkKey, this.helpdeskLink);
      }

      appLogger.setUser(userProfile);

      return userProfile;
    }
  }
});